import React from 'react'
import { styled } from '@mui/material/styles'

import Badge from '@mui/material/Badge'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'

export type AppliedFilterView = {
    caption: string
    parts: { prefix?: string; tags: string[]; hiddenCount: number }[]
}

const FilterButton = styled(IconButton, { shouldForwardProp: (prop) => prop !== 'active' })<{ active?: boolean }>(
    ({ active }) => ({
        width: 34,
        height: 34,
        flex: '0 0 auto',
        borderRadius: 8,
        color: active ? 'var(--green-800)' : 'var(--muted)',
        backgroundColor: active ? 'var(--green-050)' : 'transparent',
        '&:hover': { color: 'var(--green-800)', backgroundColor: 'var(--green-050)' },
        '& .MuiSvgIcon-root': { fontSize: 20 }
    })
)

const filterBadgeSx = {
    '& .MuiBadge-badge': {
        top: 2,
        right: 2,
        height: 14,
        minWidth: 14,
        padding: '0 4px',
        fontSize: 9.5,
        fontWeight: 700,
        backgroundColor: 'var(--green-800)',
        color: '#fff'
    }
}

const TipLabel = styled('span')({ color: 'rgba(255, 255, 255, .68)' })

const TipHead = styled('div')({
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '.4px',
    textTransform: 'uppercase',
    color: 'rgba(255, 255, 255, .5)',
    marginBottom: 8
})

const TipList = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 7,
    maxHeight: 320,
    overflowY: 'auto'
})

const TipFilterRow = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'minmax(80px, max-content) 1fr',
    gap: 10,
    alignItems: 'baseline'
})

const TagRow = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 4
})

const TagPrefix = styled('span')({ fontSize: 11, color: 'rgba(255, 255, 255, .5)' })

const Tag = styled('span')({
    borderRadius: 999,
    border: '1px solid rgba(255, 255, 255, .16)',
    backgroundColor: 'rgba(255, 255, 255, .08)',
    padding: '1px 7px',
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap'
})

const TagMore = styled(Tag)({
    color: '#9fe0a6',
    borderColor: 'rgba(159, 224, 166, .32)',
    backgroundColor: 'rgba(159, 224, 166, .12)'
})

export const infoTooltipSx = {
    backgroundColor: '#171b18',
    border: '1px solid #2d382f',
    borderRadius: '10px',
    padding: '10px 12px',
    fontSize: 12,
    boxShadow: 'var(--shadow-2)'
}

function AppliedFiltersTip({ filters }: { filters: AppliedFilterView[] }) {
    if (filters.length === 0) return <TipLabel>Sem filtro aplicado</TipLabel>
    return (
        <>
            <TipHead>{filters.length === 1 ? '1 campo filtrado' : `${filters.length} campos filtrados`}</TipHead>
            <TipList>
                {filters.map((filter, index) => (
                    <TipFilterRow key={`${filter.caption}-${index}`}>
                        <TipLabel>{filter.caption}</TipLabel>
                        <TagRow>
                            {filter.parts.map((part, partIndex) => (
                                <React.Fragment key={partIndex}>
                                    {!!part.prefix && <TagPrefix>{part.prefix}</TagPrefix>}
                                    {part.tags.map((tag, tagIndex) => (
                                        <Tag key={`${tag}-${tagIndex}`}>{tag}</Tag>
                                    ))}
                                    {part.hiddenCount > 0 && <TagMore>+{part.hiddenCount}</TagMore>}
                                </React.Fragment>
                            ))}
                        </TagRow>
                    </TipFilterRow>
                ))}
            </TipList>
        </>
    )
}

export function AppliedFiltersButton({ filters }: { filters: AppliedFilterView[] }) {
    const count = filters.length
    return (
        <Tooltip
            arrow
            placement="left"
            slotProps={{ tooltip: { sx: count > 0 ? { ...infoTooltipSx, maxWidth: 460 } : infoTooltipSx } }}
            title={<AppliedFiltersTip filters={filters} />}
        >
            <FilterButton
                size="small"
                active={count > 0}
                aria-label={count > 0 ? `${count} campos filtrados` : 'Sem filtro aplicado'}
            >
                <Badge sx={filterBadgeSx} badgeContent={count} overlap="circular">
                    <FilterAltOutlinedIcon />
                </Badge>
            </FilterButton>
        </Tooltip>
    )
}
